import { readReferenceVideo } from "@/features/matches/referenceVideo";
import { ReferenceVideoCard } from "./ReferenceVideoCard";
import type { CoachingClip, CoachingNote, Player } from "@/lib/types";
import { FeedbackTonePill } from "./FeedbackCardBits";
import {
  categoryLabel,
  contextPillLabel,
  feedbackNoteSummary,
} from "../feedbackHelpers";

/**
 * The structured-feedback body shared between the M5
 * `FeedbackPlayerModal`'s note and clip modes. Ports legacy
 * `_renderUnifiedFeedbackBody` (`js/coaching.js:5310`).
 *
 * For notes, renders: context pill (Video note / Coach observation
 * / Tactical observation) + tone pill + category chip + Reviewed ✓
 * chip when present, a "For: ⟨player⟩" linked-player row, a
 * Summary section (player_summary or body), the structured
 * What happened / Why it matters / What to do next stack, an
 * Additional detail section when player_summary was the primary
 * and `body` is distinct, and a row of `#tag` chips.
 *
 * For clips, renders: context pill ("Coaching clip") + category
 * chip + linked-player row + a Description section.
 *
 * Privacy invariant: `coach_private_note` is NEVER referenced by
 * this component. The server already scrubs it for viewers (via
 * `_strip_private_fields`); this component is defense-in-depth.
 * Always destructure the specific fields you need — never spread
 * the note.
 */

export type UnifiedFeedbackBodyProps =
  | {
      kind: "note";
      note: CoachingNote;
      players?: Player[];
      reviewed?: boolean;
      hideMeta?: boolean;
    }
  | {
      kind: "clip";
      clip: CoachingClip;
      players?: Player[];
      hideMeta?: boolean;
    };

export function UnifiedFeedbackBody(props: UnifiedFeedbackBodyProps) {
  if (props.kind === "note") {
    return (
      <NoteBody
        note={props.note}
        players={props.players ?? []}
        reviewed={!!props.reviewed}
        hideMeta={!!props.hideMeta}
      />
    );
  }
  return (
    <ClipBody
      clip={props.clip}
      players={props.players ?? []}
      hideMeta={!!props.hideMeta}
    />
  );
}

function NoteBody({
  note,
  players,
  reviewed,
  hideMeta,
}: {
  note: CoachingNote;
  players: Player[];
  reviewed: boolean;
  hideMeta: boolean;
}) {
  // Defense-in-depth: explicitly pick only the fields we render —
  // `coach_private_note` is intentionally NOT destructured.
  const {
    note_type,
    note_context,
    category,
    player_ids,
    tags,
    what_happened,
    why_it_matters,
    what_to_do_next,
  } = note;

  // Read via the shared helper rather than destructuring the five raw
  // fields — it enforces "provider and id together" so a half-written
  // reference can't render a broken card.
  const referenceVideo = readReferenceVideo(note);

  const isObservation = (note_context ?? "video") === "observation";
  const { primary, secondary } = feedbackNoteSummary(note);
  const catLabel = categoryLabel(category);

  return (
    <div
      data-testid="feedback-detail-body"
      data-context={isObservation ? "observation" : "video"}
      className="flex flex-col gap-3"
    >
      {!hideMeta ? (
        <>
          <div className="flex flex-wrap items-center gap-1.5">
            <ContextPill label={contextPillLabel(note)} />
            <FeedbackTonePill noteType={note_type} />
            {catLabel ? <DetailChip>{catLabel}</DetailChip> : null}
            {reviewed ? (
              <DetailChip data-testid="feedback-detail-reviewed-chip">
                Reviewed ✓
              </DetailChip>
            ) : null}
          </div>

          <LinkedPlayersRow playerIds={player_ids} players={players} />
        </>
      ) : null}

      {primary ? (
        <Section
          label="Summary"
          body={primary}
          testid="feedback-detail-summary"
        />
      ) : null}

      {what_happened?.trim() ? (
        <Section
          label="What happened"
          body={what_happened}
          testid="feedback-detail-what-happened"
        />
      ) : null}
      {why_it_matters?.trim() ? (
        <Section
          label="Why it matters"
          body={why_it_matters}
          testid="feedback-detail-why"
        />
      ) : null}
      {what_to_do_next?.trim() ? (
        <Section
          label="What to do next"
          body={what_to_do_next}
          testid="feedback-detail-next"
        />
      ) : null}

      {secondary ? (
        <Section
          label="Additional detail"
          body={secondary}
          testid="feedback-detail-additional"
        />
      ) : null}

      {/* Below the written feedback on purpose (#675): the coach's point is
        * why the player is here, and the video supports it. Leading with the
        * video would invert that. */}
      {referenceVideo ? <ReferenceVideoCard video={referenceVideo} /> : null}

      {tags && tags.length > 0 ? (
        <div
          className="flex flex-wrap gap-1.5"
          role="group"
          aria-label="Tags"
          data-testid="feedback-detail-tags"
        >
          {tags.map((t) => (
            <DetailChip key={t}>#{t}</DetailChip>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function ClipBody({
  clip,
  players,
  hideMeta,
}: {
  clip: CoachingClip;
  players: Player[];
  hideMeta: boolean;
}) {
  const { category, player_ids, description } = clip;
  const catLabel = categoryLabel(category);
  const desc = (description ?? "").trim();
  return (
    <div
      data-testid="feedback-detail-body"
      data-context="clip"
      className="flex flex-col gap-3"
    >
      {!hideMeta ? (
        <>
          <div className="flex flex-wrap items-center gap-1.5">
            <ContextPill label="Coaching clip" />
            {catLabel ? <DetailChip>{catLabel}</DetailChip> : null}
          </div>
          <LinkedPlayersRow playerIds={player_ids} players={players} />
        </>
      ) : null}
      {desc ? (
        <Section
          label="Description"
          body={desc}
          testid="feedback-detail-description"
        />
      ) : null}
    </div>
  );
}

function Section({
  label,
  body,
  testid,
}: {
  label: string;
  body: string;
  testid?: string;
}) {
  return (
    <section data-testid={testid}>
      <h3 className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
        {label}
      </h3>
      <p className="mt-0.5 whitespace-pre-wrap text-sm text-ink">
        {body.trim()}
      </p>
    </section>
  );
}

function LinkedPlayersRow({
  playerIds,
  players,
}: {
  playerIds: string[] | undefined;
  players: Player[];
}) {
  if (!playerIds || playerIds.length === 0) return null;
  const byId = new Map(players.map((p) => [String(p.id), p]));
  return (
    <div
      className="flex flex-wrap items-center gap-1.5 text-xs"
      data-testid="feedback-detail-linked"
    >
      <span className="font-display uppercase tracking-wider text-ink-muted">
        For:
      </span>
      {playerIds.map((pid) => {
        const p = byId.get(String(pid));
        const label = p
          ? p.jersey_number
            ? `#${p.jersey_number} ${p.display_name}`
            : p.display_name
          : "Linked player";
        return (
          <span
            key={pid}
            className="rounded border border-rule bg-surface px-1.5 py-0.5 text-ink"
          >
            {label}
          </span>
        );
      })}
    </div>
  );
}

function ContextPill({ label }: { label: string }) {
  return (
    <span
      data-testid="feedback-detail-context-pill"
      className="rounded border border-rule bg-surface px-1.5 py-0.5 text-[0.6rem] font-display uppercase tracking-wider text-ink"
    >
      {label}
    </span>
  );
}

function DetailChip({
  children,
  ...rest
}: React.HTMLAttributes<HTMLSpanElement>) {
  return (
    <span
      {...rest}
      className="rounded px-1.5 py-0.5 text-[0.6rem] font-display uppercase tracking-wider bg-surface text-ink-muted"
    >
      {children}
    </span>
  );
}
